import { useTranslation } from "react-i18next";
import { status, STATUS_MODEL } from "../../../../enums/statusEnum";
import TextTypeButton from './TextTypeButton';
import CustomChip from "../../../../components/CustomChip";
import { statusBorderColor, statusColor, statusFontColor } from "../../../../enums/statusColorEnum";
import CustomCard from "../../../../components/CustomCard";
import { CustomMdChip } from "../../../../components/Styles";
import CustomTooltip from "../../../../components/CustomTooltip";


/**
 * @Email    : sgarcia@ibl.mx
 * @Author   : Susana Garcia Rodriguez
 * @JobTitle : Frontend Developer
 *
 * @Description
 * TextCard is a reusable card component designed to display an icon,
 * a primary title with ellipsis handling, an optional subtitle, and optional
 * actions such as a status chip or a text button. It is commonly used to display
 * summarized information in list or detail views.
 *
 * @param {React.Component} Icon - Leading icon component for the card.
 * @param {string} title - Main title text displayed with ellipsis and tooltip.
 * @param {JSX.Element|string} subtitle - Secondary text or JSX element below the title.
 * @param {string} classContent - Additional CSS classes for the card content section.
 * @param {boolean} showChip - Whether to display the status chip on the right.
 * @param {boolean} showText - Whether to display the optional text button.
 * @param {string} classes - CSS classes applied to the main CustomCard wrapper.
 * @param {string} text - Text passed to the CustomTextButton when enabled.
 * @param {string|number} statusId - Status identifier used to determine chip colors and translated label.
 * @param {boolean} [disabled=false]  When true, the button becomes visually disabled and non-interactive.
 * @param {JSX.Element|null} [footer=null] - Optional footer element displayed at the bottom of the card.
 * @param {boolean} [copyOnClick=false] - Enables copying the text value when the text button is clicked.
 */
const TextCard = ({
    Icon,
    title,
    subtitle,
    classContent = '',
    showChip = false,
    showText = false,
    classes = 'card-shades-100 border-radius-8px',
    text,
    statusId = STATUS_MODEL.PENDING,
    footer,
    disabled,
    copyOnClick
}) => {
    const { t } = useTranslation();

    const renderTextButton = () => showText && <TextTypeButton text={text} disabled={disabled} copyOnClick={copyOnClick} />;

    const renderChip = () =>
        showChip ? (
            <div className='pl-3 pr-3'>
                <CustomChip
                    variant={'large'}
                    background={statusColor[statusId]}
                    fontColor={statusFontColor[statusId]}
                    borderColor={statusBorderColor[statusId]}
                    legend={t(status[statusId]) || t('pending')}

                />
            </div>
        ) : (<></>)
    return (
        <CustomCard
            classes={classes}
            classContent={classContent}
            children={
                <>
                <div className={'d-flex justify-content-start align-items-center p-0'}>
                    <div className='flex-grow-1 minw-0 ml-2'>
                        <div className={'flex-grow-1 minw-0'}>
                            <CustomTooltip
                                text={title}
                                classes={`fs-14px font-color-general-950 ibl-font-w-600 text-ellipsis-1-row`}
                                showIfTruncated={true}
                                icon={Icon}
                            />
                        </div>
                        {subtitle}
                    </div>

                    <div className={'d-flex-end gap-2'}>
                        {renderChip()}
                        {renderTextButton()}
                    </div>
                </div>
                {footer && <div className='mt-auto pt-2'>{footer}</div>}
                </>
            }
        />
    );
};

export default TextCard;
